import { createStorefrontApiClient } from "@shopify/storefront-api-client";

// Initialize the Storefront API Client
export const client = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
  publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
  apiVersion: '2025-10',
});

const DEFAULT_COUNTRY_CODE = process.env.GATSBY_SHOPIFY_COUNTRY_CODE || 'GB';

// Try to detect user's country from browser
const detectUserCountry = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_COUNTRY_CODE;
  }
  
  // Check if we have a stored preference
  try {
    const stored = localStorage.getItem('shopify_country_code');
    if (stored) return stored;
  } catch (e) {
    // localStorage might be disabled
  }
  
  // Try to detect from timezone or language
  try {
    // Get timezone offset as a proxy for country (not perfect but helpful)
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    
    // Simple mapping of common timezones to country codes
    const tzToCountry = {
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU',
      'Australia/Adelaide': 'AU',
      'Australia/Darwin': 'AU',
      'Australia/Hobart': 'AU',
      'Europe/London': 'GB',
      'Europe/Dublin': 'IE',
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
    };
    
    if (tzToCountry[tz]) {
      return tzToCountry[tz];
    }
  } catch (e) {
    // Timezone detection failed
  }
  
  return DEFAULT_COUNTRY_CODE;
};

const toArray = (value) => Array.isArray(value) ? value : [];

const formatUserErrors = (userErrors = []) => userErrors
  .map(({ field, message }) => {
    const fieldPath = Array.isArray(field) && field.length > 0 ? ` (${field.join('.')})` : '';
    return `${message}${fieldPath}`;
  })
  .join('; ');

const formatWarnings = (warnings = []) => warnings
  .map(({ code, message, target }) => {
    const codeLabel = code ? `${code}: ` : '';
    const targetLabel = target ? ` (${target})` : '';
    return `${codeLabel}${message}${targetLabel}`;
  })
  .join('; ');

const assertNoUserErrors = (payload, key) => {
  const userErrors = toArray(payload?.[key]?.userErrors);
  if (userErrors.length > 0) {
    throw new Error(formatUserErrors(userErrors));
  }
};

// Shopify's `warnings` (e.g. inventory/market advisories) are non-fatal and
// distinct from `userErrors` — the mutation still succeeds, so we only log them.
const logWarnings = (payload, key) => {
  const warnings = toArray(payload?.[key]?.warnings);
  if (warnings.length > 0) {
    logCartDebug(`${key}:warnings`, { warnings: formatWarnings(warnings) });
  }
};

// A cart mutation can report no userErrors and still not add the requested
// quantity (e.g. MERCHANDISE_OUT_OF_STOCK is only reported as a warning) —
// find the lines that didn't land so callers don't show a false success.
const findShortfallMerchandiseIds = (cart, requestedLines) => {
  const requestedByMerchandise = requestedLines.reduce((acc, { merchandiseId, quantity }) => {
    acc[merchandiseId] = (acc[merchandiseId] || 0) + quantity;
    return acc;
  }, {});

  const actualByMerchandise = toArray(cart?.lines?.edges).reduce((acc, edge) => {
    const merchandiseId = edge?.node?.merchandise?.id;
    if (merchandiseId) {
      acc[merchandiseId] = (acc[merchandiseId] || 0) + (edge?.node?.quantity || 0);
    }
    return acc;
  }, {});

  return Object.entries(requestedByMerchandise)
    .filter(([merchandiseId, quantity]) => (actualByMerchandise[merchandiseId] || 0) < quantity)
    .map(([merchandiseId]) => merchandiseId);
};

// Cart mutations are scoped to the buyer's country, but this query is not, so a
// variant that reads as available here is stocked somewhere — it just can't be
// shipped to the buyer.
const isStockedOutsideBuyerContext = async (merchandiseIds) => {
  try {
    const { data } = await client.request(VARIANT_AVAILABILITY_QUERY, {
      variables: { ids: merchandiseIds },
    });
    return toArray(data?.nodes).some((node) => node?.availableForSale);
  } catch (error) {
    logCartDebug('variantAvailability:failed', { message: error?.message });
    return false;
  }
};

// Country names that read as "ship to the United Kingdom", not "to United Kingdom".
const NEEDS_DEFINITE_ARTICLE = /^(United |Netherlands|Philippines|Bahamas|Maldives|Gambia|Ivory Coast|(Czech|Dominican|Central African|Democratic) Republic)/;

const countryLabel = (countryCode) => {
  if (!countryCode) {
    return 'your region';
  }

  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode);
    if (!name) {
      return countryCode;
    }
    return NEEDS_DEFINITE_ARTICLE.test(name) ? `the ${name}` : name;
  } catch (e) {
    return countryCode;
  }
};

// Shopify reports MERCHANDISE_OUT_OF_STOCK whenever no location can ship the
// variant to the buyer's country, including when the variant has stock — so
// check the unscoped availability before telling a customer it's sold out.
const describeShortfall = async (merchandiseIds, warnings, countryCode) => {
  const soldOut = toArray(warnings).some(({ code }) => code === 'MERCHANDISE_OUT_OF_STOCK');

  if (soldOut) {
    const stockedElsewhere = await isStockedOutsideBuyerContext(merchandiseIds);
    return stockedElsewhere
      ? `We're currently not able to ship this to ${countryLabel(countryCode)}.`
      : 'This item is sold out.';
  }

  return toArray(warnings).length > 0
    ? formatWarnings(warnings)
    : 'This item could not be added to your cart. It may be out of stock.';
};

const getBuyerIdentity = (countryCode) => ({
  countryCode: countryCode || detectUserCountry(),
});

const logCartDebug = (event, details = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  console.log(`[shopify-cart] ${event}`, details);
  if (details.requestedCountryCode || details.returnedCountryCode) {
    console.log(
      `[shopify-cart] ${event} country`,
      `requested=${details.requestedCountryCode || 'n/a'}`,
      `returned=${details.returnedCountryCode || 'n/a'}`
    );
  }
};

const normalizeMerchandiseId = (value) => {
  if (typeof value !== 'string') {
    throw new Error('Missing Shopify variant ID for cart line');
  }

  const trimmed = value.trim();

  if (trimmed.startsWith('gid://shopify/ProductVariant/')) {
    return trimmed;
  }

  if (/^\d+$/.test(trimmed)) {
    return `gid://shopify/ProductVariant/${trimmed}`;
  }

  throw new Error(`Invalid Shopify variant ID: ${trimmed}`);
};

const normalizeCartLines = (lines = []) => lines.map((line) => ({
  ...line,
  merchandiseId: normalizeMerchandiseId(line.merchandiseId),
  quantity: Number.parseInt(line.quantity, 10),
})).filter((line) => Number.isInteger(line.quantity) && line.quantity > 0);

// GraphQL Queries
// Deliberately has no @inContext directive: it reports availability across the
// whole shop, which is what distinguishes "sold out" from "can't ship here".
export const VARIANT_AVAILABILITY_QUERY = `
  query variantAvailability($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        availableForSale
        quantityAvailable
      }
    }
  }
`;

export const CART_CREATE_MUTATION = `
  mutation cartCreate($cartInput: CartInput) {
    cartCreate(input: $cartInput) {
      cart {
        id
        checkoutUrl
        buyerIdentity {
          countryCode
        }
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                  }
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const CART_FETCH_QUERY = `
  query cartQuery($cartId: ID!) {
    cart(id: $cartId) {
      id
      checkoutUrl
      buyerIdentity {
        countryCode
      }
      totalQuantity
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                product {
                  id
                  handle
                  title
                }
                price {
                  amount
                  currencyCode
                }
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      }
      cost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
      }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        buyerIdentity {
          countryCode
        }
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                  }
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
      warnings {
        code
        message
        target
      }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        buyerIdentity {
          countryCode
        }
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                  }
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
      warnings {
        code
        message
        target
      }
    }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
        buyerIdentity {
          countryCode
        }
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                  }
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
      warnings {
        code
        message
        target
      }
    }
  }
`;

export const CART_BUYER_IDENTITY_UPDATE_MUTATION = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        checkoutUrl
        buyerIdentity {
          countryCode
        }
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                  }
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
      warnings {
        code
        message
        target
      }
    }
  }
`;

export const PRODUCT_QUERY = `
  query getProduct($handle: String!) {
    product(handle: $handle) {
      id
      handle
      title
      description
      variants(first: 100) {
        edges {
          node {
            id
            title
            availableForSale
            quantityAvailable
            price {
              amount
              currencyCode
            }
            selectedOptions {
              name
              value
            }
            image {
              url
              altText
            }
          }
        }
      }
      images(first: 20) {
        edges {
          node {
            url
            altText
          }
        }
      }
    }
  }
`;

export const PRODUCT_BY_ID_QUERY = `
  query getProductById($id: ID!) {
    product(id: $id) {
      id
      handle
      title
      description
      variants(first: 100) {
        edges {
          node {
            id
            title
            availableForSale
            quantityAvailable
            price {
              amount
              currencyCode
            }
            selectedOptions {
              name
              value
            }
            image {
              url
              altText
            }
          }
        }
      }
      images(first: 20) {
        edges {
          node {
            url
            altText
          }
        }
      }
    }
  }
`;

// Legacy checkout creation using Storefront API checkoutCreate mutation
export const CHECKOUT_CREATE_MUTATION = `
  mutation checkoutCreate($input: CheckoutCreateInput!) {
    checkoutCreate(input: $input) {
      checkout {
        id
        webUrl
        totalTaxV2 {
          amount
          currencyCode
        }
        totalPriceV2 {
          amount
          currencyCode
        }
        subtotalPriceV2 {
          amount
          currencyCode
        }
        lineItems(first: 250) {
          edges {
            node {
              id
              title
              quantity
              variant {
                id
                title
                price
                priceV2 {
                  amount
                  currencyCode
                }
                product {
                  id
                  handle
                  title
                }
              }
            }
          }
        }
      }
      checkoutUserErrors {
        field
        message
      }
    }
  }
`;

// Create a checkout session from cart (might give us the proper checkout ID)
export const createCheckoutFromCart = async (cartId) => {
  const CHECKOUT_CREATE_MUTATION = `
    mutation checkoutCreate($input: CheckoutCreateInput!) {
      checkoutCreate(input: $input) {
        checkout {
          id
          webUrl
          completedAt
          createdAt
          currencyCode
          totalPrice {
            amount
            currencyCode
          }
          lineItems(first: 250) {
            edges {
              node {
                id
                title
                quantity
                variant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        checkoutUserErrors {
          field
          message
        }
      }
    }
  `;

  try {
    // First get the cart to extract line items
    const cartData = await fetchCart(cartId);
    if (!cartData || !cartData.lineItems) {
      throw new Error('No cart data found');
    }

    // Convert cart line items to checkout input format
    const lineItems = cartData.lineItems.map(item => ({
      variantId: item.variant.id,
      quantity: item.quantity
    }));

    const { data } = await client.request(CHECKOUT_CREATE_MUTATION, {
      variables: {
        input: {
          lineItems
        }
      }
    });

    if (data.checkoutCreate.checkoutUserErrors.length > 0) {
      throw new Error('Checkout creation failed: ' + data.checkoutCreate.checkoutUserErrors[0].message);
    }

    console.log('✅ Created checkout session:', data.checkoutCreate.checkout);
    return data.checkoutCreate.checkout;
  } catch (error) {
    console.error('❌ Error creating checkout:', error);
    return null;
  }
};

// Helper functions to mimic JS Buy SDK behavior
export const createCart = async () => {
  logCartDebug('createCart:start', {
    requestedCountryCode: DEFAULT_COUNTRY_CODE,
  });

  const { data } = await client.request(CART_CREATE_MUTATION, {
    variables: {
      cartInput: {
        buyerIdentity: getBuyerIdentity(),
      }
    }
  });

  assertNoUserErrors(data, 'cartCreate');
  logWarnings(data, 'cartCreate');
  
  // Transform the cart to match JS Buy SDK structure
  const cart = data.cartCreate.cart;
  logCartDebug('createCart:success', {
    cartId: cart?.id,
    returnedCountryCode: cart?.buyerIdentity?.countryCode,
  });
  return transformCartData(cart);
};

export const fetchCart = async (cartId) => {
  const { data } = await client.request(CART_FETCH_QUERY, {
    variables: {
      cartId
    }
  });

  if (!data?.cart) {
    throw new Error('Cart not found');
  }

  logCartDebug('fetchCart:success', {
    cartId: data.cart.id,
    returnedCountryCode: data.cart?.buyerIdentity?.countryCode,
  });
  
  // Transform the cart to match JS Buy SDK structure
  return transformCartData(data.cart);
};

export const addLineItems = async (cartId, lines) => {
  const normalizedLines = normalizeCartLines(lines);

  logCartDebug('addLineItems:start', {
    cartId,
    requestedCountryCode: DEFAULT_COUNTRY_CODE,
    lines: normalizedLines,
  });

  if (normalizedLines.length === 0) {
    throw new Error('No valid cart lines to add');
  }

  const { data } = await client.request(CART_LINES_ADD_MUTATION, {
    variables: {
      cartId,
      lines: normalizedLines
    }
  });

  assertNoUserErrors(data, 'cartLinesAdd');
  logWarnings(data, 'cartLinesAdd');

  const shortfallMerchandiseIds = findShortfallMerchandiseIds(data?.cartLinesAdd?.cart, normalizedLines);
  if (shortfallMerchandiseIds.length > 0) {
    throw new Error(await describeShortfall(
      shortfallMerchandiseIds,
      data?.cartLinesAdd?.warnings,
      data?.cartLinesAdd?.cart?.buyerIdentity?.countryCode || DEFAULT_COUNTRY_CODE,
    ));
  }

  logCartDebug('addLineItems:success', {
    cartId: data?.cartLinesAdd?.cart?.id,
    returnedCountryCode: data?.cartLinesAdd?.cart?.buyerIdentity?.countryCode,
    totalQuantity: data?.cartLinesAdd?.cart?.totalQuantity,
  });
  
  // Transform the cart to match JS Buy SDK structure
  return transformCartData(data.cartLinesAdd.cart);
};

export const updateLineItems = async (cartId, lines) => {
  const { data } = await client.request(CART_LINES_UPDATE_MUTATION, {
    variables: {
      cartId,
      lines
    }
  });

  assertNoUserErrors(data, 'cartLinesUpdate');
  logWarnings(data, 'cartLinesUpdate');
  
  // Transform the cart to match JS Buy SDK structure
  return transformCartData(data.cartLinesUpdate.cart);
};

export const removeLineItems = async (cartId, lineIds) => {
  const { data } = await client.request(CART_LINES_REMOVE_MUTATION, {
    variables: {
      cartId,
      lineIds
    }
  });

  assertNoUserErrors(data, 'cartLinesRemove');
  logWarnings(data, 'cartLinesRemove');
  
  // Transform the cart to match JS Buy SDK structure
  return transformCartData(data.cartLinesRemove.cart);
};

export const fetchProduct = async (productId) => {
  const { data } = await client.request(PRODUCT_BY_ID_QUERY, {
    variables: {
      id: productId
    }
  });
  return data.product;
};

export const updateCartBuyerIdentity = async (cartId, countryCode = DEFAULT_COUNTRY_CODE) => {
  logCartDebug('updateCartBuyerIdentity:start', {
    cartId,
    requestedCountryCode: countryCode,
  });

  const { data } = await client.request(CART_BUYER_IDENTITY_UPDATE_MUTATION, {
    variables: {
      cartId,
      buyerIdentity: getBuyerIdentity(countryCode),
    }
  });

  assertNoUserErrors(data, 'cartBuyerIdentityUpdate');
  logWarnings(data, 'cartBuyerIdentityUpdate');

  logCartDebug('updateCartBuyerIdentity:success', {
    cartId: data?.cartBuyerIdentityUpdate?.cart?.id,
    returnedCountryCode: data?.cartBuyerIdentityUpdate?.cart?.buyerIdentity?.countryCode,
  });

  return transformCartData(data.cartBuyerIdentityUpdate.cart);
};

// Use checkout URL directly from Storefront API as recommended by Shopify
const getCheckoutUrl = (cart) => {
  // Follow official Shopify migration guide: use checkoutUrl directly from the API
  return cart.checkoutUrl || cart.webUrl || null;
};

// Transform cart data to match JS Buy SDK structure
const transformCartData = (cart) => {
  if (!cart) return null;
  
  // Transform line items from GraphQL structure to JS Buy SDK structure
  const lineItems = cart.lines?.edges ? cart.lines.edges.map(edge => ({
    id: edge.node.id,
    quantity: edge.node.quantity,
    title: edge.node.merchandise.product.title,
    variant: {
      id: edge.node.merchandise.id,
      title: edge.node.merchandise.title,
      price: {
        amount: edge.node.merchandise.price.amount,
        currencyCode: edge.node.merchandise.price.currencyCode
      },
      product: {
        id: edge.node.merchandise.product.id,
        handle: edge.node.merchandise.product.handle,
        title: edge.node.merchandise.product.title
      },
      image: edge.node.merchandise.image ? {
        ...edge.node.merchandise.image,
        src: edge.node.merchandise.image.url,
      } : null
    }
  })) : [];
  
  // Get the total price amount, handling different response formats
  const totalPriceAmount = cart.cost?.totalAmount?.amount || cart.totalPrice?.amount || cart.totalPrice || '0';
  const subtotalPriceAmount = cart.cost?.subtotalAmount?.amount || cart.subtotalPrice?.amount || cart.subtotalPrice || '0';
  const currencyCode = cart.cost?.totalAmount?.currencyCode || cart.totalPrice?.currencyCode || 'USD';
  
  // Get checkout URL from the API
  const checkoutUrl = getCheckoutUrl(cart);
  
  return {
    id: cart.id,
    checkoutUrl: checkoutUrl,
    webUrl: checkoutUrl, // For backward compatibility
    buyerIdentity: cart.buyerIdentity || getBuyerIdentity(),
    totalQuantity: cart.totalQuantity,
    lineItems,
    totalPrice: totalPriceAmount,
    subtotalPrice: subtotalPriceAmount,
    currencyCode: currencyCode
  };
};

// Helper function to find variant by options (similar to JS Buy SDK's variantForOptions)
export const variantForOptions = (product, options) => {
  if (!product || !product.variants || !options) return null;
  
  // Handle both Gatsby and direct Shopify data structures
  const variants = product.variants.edges ? product.variants.edges.map(edge => edge.node) : product.variants;
  
  return variants.find(variant => {
    if (!variant.selectedOptions) return false;
    
    return variant.selectedOptions.every(option => {
      return options[option.name] === option.value;
    });
  });
};

// Add a test function to create a fresh checkout
export const createTestCheckout = async () => {
  console.log('🧪 Creating test checkout...');
  try {
    const { data } = await client.request(CART_CREATE_MUTATION, {
      variables: {
        cartInput: {
          lines: [
            {
              merchandiseId: 'gid://shopify/ProductVariant/YOUR_TEST_VARIANT_ID',
              quantity: 1
            }
          ]
        }
      }
    });
    
    console.log('Test checkout created:', data);
    return data.cartCreate.cart;
  } catch (error) {
    console.error('Error creating test checkout:', error);
    return null;
  }
};
